import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import S3Uploader from "@/components/S3Uploader";
import {
  Loader2, Download, FileText, FileImage, FileVideo, FileSpreadsheet,
  File as FileIcon, Paperclip, Eye, X, ChevronDown, Play,
} from "lucide-react";

function humanSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function isImage(f) { return (f.content_type || "").startsWith("image/"); }
function isPreviewable(f) { const ct = f.content_type || ""; return ct.startsWith("image/") || ct === "application/pdf"; }

function iconFor(f) {
  const ct = f.content_type || "";
  if (ct.startsWith("image/")) return FileImage;
  if (ct.startsWith("video/")) return FileVideo;
  if (ct === "application/pdf") return FileText;
  if (ct.includes("spreadsheet") || ct.includes("excel") || ct.includes("csv")) return FileSpreadsheet;
  if (ct.includes("word") || ct === "text/plain" || ct.includes("document")) return FileText;
  return FileIcon;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Authenticated blob preview for legacy image files
function Thumb({ file, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null, cancelled = false;
    api.get(`/files/${file.path}`, { responseType: "blob" })
      .then((r) => { if (!cancelled) { revoked = URL.createObjectURL(r.data); setUrl(revoked); } })
      .catch(() => {});
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [file.path]);
  if (!url) return <div className={`bg-slate-100 flex items-center justify-center ${className}`}><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  return <img src={url} alt={file.filename} className={`object-cover ${className}`} />;
}

// Task files: S3 attachments are the PRIMARY, secure, malware-scanned source.
// Legacy files are shown READ-ONLY beneath as a temporary fallback during migration.
export default function TaskFiles({ legacyId }) {
  const [open, setOpen] = useState(false);
  const { can } = useAuth();
  const canManage = can("orders.manage");
  const [legacy, setLegacy] = useState([]);
  const [field, setField] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [fieldPreview, setFieldPreview] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/tasks/${legacyId}/all-media`)
      .then((r) => setField((r.data.items || []).filter((x) => x.source === "mobile")))
      .catch(() => setField([]));
    api.get(`/tasks/${legacyId}/files`)
      .then((r) => setLegacy(r.data.items || []))
      .catch(() => setLegacy([]))
      .finally(() => setLoading(false));
  }, [legacyId]);

  const download = async (f) => {
    try {
      const r = await api.get(`/files/${f.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="task-files">
      <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-50/70 border-b border-slate-100">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left text-base font-bold text-slate-900" data-testid="task-files-toggle">
          <Paperclip className="w-4 h-4 text-indigo-500" /> Files
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-600" aria-label="Toggle Files">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>

      {open && (<div className="px-5 py-5">
      {/* PRIMARY: secure S3 attachments (scan-gated) */}
      <S3Uploader recordType="tasks" recordId={legacyId} category="Task Attachment" canManage={canManage} serverSide />

      {/* Field & app media synced from the mobile app (record_type=job_media), read-only here */}
      {field.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="task-files-field">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
            Field &amp; app media ({field.length}) · synced from the mobile app
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-testid="task-field-media-list">
            {field.map((f) => <FieldMediaTile key={f.id} item={f} onOpen={setFieldPreview} />)}
          </div>
        </div>
      )}

      {/* Legacy read-only fallback (retained during migration; do not delete) */}
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading legacy files…</div>
      ) : legacy.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="task-files-legacy">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
            Legacy files ({legacy.length}) · read-only (retained as fallback during migration)
          </div>
          <div className="space-y-1.5" data-testid="task-files-list">
            {legacy.map((f) => {
              const Icon = iconFor(f);
              return (
                <div key={f.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors" data-testid={`task-file-${f.id}`}>
                  {isImage(f) ? (
                    <button onClick={() => setPreview(f)} className="shrink-0" title="Preview"><Thumb file={f} className="w-9 h-9 rounded-md" /></button>
                  ) : <Icon className="w-5 h-5 text-slate-400 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    {isPreviewable(f) ? (
                      <button onClick={() => setPreview(f)} className="text-sm font-medium text-slate-800 truncate hover:text-indigo-600 hover:underline text-left block w-full" title={f.filename} data-testid={`task-file-open-${f.id}`}>{f.filename}</button>
                    ) : (
                      <div className="text-sm font-medium text-slate-800 truncate" title={f.filename}>{f.filename}</div>
                    )}
                    <div className="text-[11px] text-slate-400 truncate">{[humanSize(f.size), f.uploaded_by, fmtDate(f.uploaded_at), "legacy"].filter(Boolean).join(" · ")}</div>
                  </div>
                  {isPreviewable(f) && <button onClick={() => setPreview(f)} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Preview" data-testid={`task-file-preview-${f.id}`}><Eye className="w-4 h-4" /></button>}
                  <button onClick={() => download(f)} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Download" data-testid={`task-file-download-${f.id}`}><Download className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>)}
      {preview && <FilePreview file={preview} onClose={() => setPreview(null)} />}
      {fieldPreview && <FieldMediaPreview item={fieldPreview} onClose={() => setFieldPreview(null)} />}
    </section>
  );
}

function FilePreview({ file, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revoked = null, cancelled = false;
    api.get(`/files/${file.path}`, { responseType: "blob" })
      .then((r) => { if (!cancelled) { revoked = URL.createObjectURL(r.data); setUrl(revoked); } })
      .catch(() => setErr(true));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); window.removeEventListener("keydown", onKey); };
  }, [file.path, onClose]);
  const pdf = (file.content_type || "").includes("pdf");
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="task-file-preview-modal">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-800 truncate pr-3">{file.filename}</div>
          <button onClick={onClose} aria-label="Close" className="p-1 text-slate-400 hover:text-slate-700 shrink-0" data-testid="task-file-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center min-h-[300px]">
          {err ? <div className="text-sm text-slate-400 p-8">Could not load preview.</div>
            : !url ? <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
            : pdf ? <iframe title={file.filename} src={url} className="w-full h-[75vh]" />
            : <img src={url} alt={file.filename} className="max-w-full max-h-[80vh] object-contain" />}
        </div>
      </div>
    </div>
  );
}

// Thumbnail tile for a field/app (mobile) media attachment — fetches a short-lived signed URL.
export function FieldMediaTile({ item, onOpen }) {
  const [url, setUrl] = useState(null);
  const isImg = (item.mime || "").startsWith("image/");
  const isVid = (item.mime || "").startsWith("video/");
  useEffect(() => {
    let cancelled = false;
    if (item.downloadable && (isImg || isVid)) {
      api.get(`/attachments/${item.id}/view`)
        .then((r) => { if (!cancelled) setUrl(r.data?.url || null); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [item.id, item.downloadable, isImg, isVid]);
  const badge = item.downloadable ? null : (item.scan_status === "SCANNING" ? "Scanning…" : "Blocked");
  const Icon = iconFor({ content_type: item.mime });
  return (
    <button
      type="button"
      onClick={() => item.downloadable && onOpen(item)}
      disabled={!item.downloadable}
      title={item.filename}
      data-testid={`task-field-media-${item.id}`}
      className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center hover:border-indigo-300 hover:shadow-sm transition-all disabled:cursor-default"
    >
      {url && isImg ? (
        <img src={url} alt={item.filename} className="w-full h-full object-cover" loading="lazy" />
      ) : url && isVid ? (
        <>
          <video src={url} className="w-full h-full object-cover" muted preload="metadata" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/15 transition-colors">
            <Play className="w-7 h-7 text-white drop-shadow" />
          </span>
        </>
      ) : (
        <Icon className="w-8 h-8 text-slate-300" />
      )}
      {badge ? (
        <span className="absolute inset-x-0 bottom-0 text-[10px] font-medium text-amber-700 bg-amber-50/95 py-0.5 text-center">{badge}</span>
      ) : (
        <span className="absolute inset-x-0 bottom-0 text-[10px] text-white bg-black/50 py-0.5 px-1.5 truncate text-left">{item.filename}</span>
      )}
      {item.marketing_permission && <span className={`absolute top-1 left-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${MKT[item.marketing_permission]?.cls}`} data-testid={`task-field-media-mkt-${item.id}`}>{MKT[item.marketing_permission]?.short}</span>}
    </button>
  );
}

// Popup preview for a field/app media attachment with a Download button.
const MKT = {
  not_confirmed: { label: "Marketing: Not Confirmed", short: "Not confirmed", cls: "bg-slate-100 text-slate-600" },
  approved: { label: "Marketing: Approved", short: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Marketing: Declined", short: "Declined", cls: "bg-rose-100 text-rose-700" },
};
function MarketingSelect({ item, onChanged }) {
  const [v, setV] = useState(item.marketing_permission);
  const save = async (nv) => {
    try { await api.put(`/attachments/${item.id}/marketing-permission`, { permission: nv }); setV(nv); item.marketing_permission = nv; onChanged?.(); toast.success("Marketing permission updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  return (
    <select value={v} onChange={(e) => save(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs" data-testid="task-field-media-marketing">
      {Object.entries(MKT).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
    </select>
  );
}
export function FieldMediaPreview({ item, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const isVid = (item.mime || "").startsWith("video/");
  const pdf = (item.mime || "").includes("pdf");
  useEffect(() => {
    let cancelled = false;
    api.get(`/attachments/${item.id}/view`)
      .then((r) => { if (!cancelled) setUrl(r.data?.url || null); })
      .catch(() => { if (!cancelled) setErr(true); });
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { cancelled = true; window.removeEventListener("keydown", onKey); };
  }, [item.id, onClose]);
  const doDownload = async () => {
    try {
      const r = await api.get(`/attachments/${item.id}/view?download=1`);
      if (r.data?.url) window.open(r.data.url, "_blank", "noopener");
    } catch { toast.error("Download not available (malware scan pending)"); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="task-field-media-modal">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-800 truncate">{item.filename}</div>
          <div className="flex items-center gap-1 shrink-0">
            {item.marketing_permission && <MarketingSelect item={item} />}
            <button onClick={doDownload} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-3 py-1.5 transition-colors" data-testid="task-field-media-download"><Download className="w-4 h-4" /> Download</button>
            <button onClick={onClose} aria-label="Close" className="p-1 text-slate-400 hover:text-slate-700" data-testid="task-field-media-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-900/95 flex items-center justify-center min-h-[300px]">
          {err ? <div className="text-sm text-slate-300 p-8">Could not load preview.</div>
            : !url ? <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
            : isVid ? <video src={url} controls autoPlay className="max-w-full max-h-[80vh]" />
            : pdf ? <iframe title={item.filename} src={url} className="w-full h-[75vh] bg-white" />
            : <img src={url} alt={item.filename} className="max-w-full max-h-[80vh] object-contain" />}
        </div>
      </div>
    </div>
  );
}
